/**
 * A wallet's own button (Apple Pay or Google Pay) drawn by Stripe's Express Checkout Element, and
 * what happens when the person approves in the wallet's sheet. What that does is the caller's `flow`:
 * saving the card (`useWalletSetup`, a SetupIntent) or paying an upgrade (`useWalletPayment`, a
 * PaymentIntent). Either way it runs in three parts — `start` before anything reaches Stripe,
 * `confirm` with Stripe.js, then `finish` with the portal — and if only `finish` fails, `retry` runs
 * it again without a second trip through the wallet.
 *
 * Apple Pay only: with `recurring` (the next renewal) Apple Pay issues a merchant token, which keeps
 * working when the person changes phone; without it a token tied to the device.
 */
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { portal } from "@/lib/api";
import {
  loadStripe,
  walletButtonOptions,
  type AvailableWallets,
  type StripeElements,
  type StripeExpressCheckoutElement,
  type StripeJs,
  type Wallet,
} from "@/lib/stripe";

/** What Apple Pay shows about the renewals the card will pay for. */
export interface ApplePayRecurring {
  /** e.g. "Growth plan" */
  label: string;
  /** Each renewal, VAT included, in pence. */
  amountCents: number;
  interval: "month" | "year";
  /** The first renewal (ISO date). */
  startsAt: string;
}

/** loading · ready (the wallet's button is drawn) · unavailable (not here: no card in the wallet,
 * the site isn't registered with Stripe, or payments aren't connected). */
export type WalletStatus = "loading" | "ready" | "unavailable";

export interface WalletEntry {
  status: WalletStatus;
  /** Where the wallet's button is mounted. */
  slot: (node: HTMLDivElement | null) => (() => void) | undefined;
  /** Between the person approving in the wallet's sheet and `finish` being done. */
  saving: boolean;
  error: unknown;
  /** Set when Stripe has it but `finish` failed: runs `finish` again. */
  retry: (() => void) | null;
}

export interface WalletFlow<Started> {
  /** Before Stripe: what confirming needs (an intent's client secret). Failing closes the sheet. */
  start: () => Promise<Started>;
  /** Stripe.js confirms what the person approved; returns what `finish` takes. */
  confirm: (stripe: StripeJs, elements: StripeElements, started: Started) => Promise<string>;
  /** The portal keeps the card, or takes the payment. */
  finish: (token: string) => Promise<void>;
}

export const WALLET_NAMES: Record<Wallet, string> = { apple_pay: "Apple Pay", google_pay: "Google Pay" };

/** How long to wait for Stripe to say whether the wallet's button shows before saying it doesn't. */
export const WALLET_WAIT_MS = 10_000;

export function useWalletButton<Started>(
  orgId: string,
  wallet: Wallet,
  {
    recurring = null,
    elementsOptions,
    incomplete,
    flow,
  }: {
    recurring?: ApplePayRecurring | null;
    /** Stripe Elements options (setup or payment mode). Compared by value: a new object with the
     * same options doesn't redraw the button. */
    elementsOptions: Record<string, unknown>;
    /** Said when the wallet's sheet didn't finish. */
    incomplete: string;
    flow: WalletFlow<Started>;
  },
): WalletEntry {
  const methods = useQuery({
    queryKey: ["payment-methods", orgId],
    queryFn: () => portal.paymentMethods(orgId),
  });
  const provider = methods.data?.provider;
  const key = methods.data?.publishable_key ?? null;
  const [stripe, setStripe] = useState<StripeJs | null>(null);
  const [status, setStatus] = useState<WalletStatus>("loading");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  /** What Stripe has that the portal couldn't finish yet (`confirm`'s result). */
  const [unfinished, setUnfinished] = useState<string | null>(null);
  const busy = useRef(false);
  // Read when the person approves, so a new flow (or message) doesn't redraw the wallet's button.
  const flowRef = useRef(flow);
  const incompleteRef = useRef(incomplete);
  useEffect(() => {
    flowRef.current = flow;
    incompleteRef.current = incomplete;
  });

  useEffect(() => {
    if (provider !== "stripe" || !key) return;
    let live = true;
    loadStripe(key).then(
      (s) => live && setStripe(s),
      (e: unknown) => {
        if (!live) return;
        setStatus("unavailable");
        setError(e);
      },
    );
    return () => {
      live = false;
    };
  }, [provider, key]);

  const finish = useCallback(async (token: string) => {
    try {
      await flowRef.current.finish(token);
      setUnfinished(null);
    } catch (err) {
      setUnfinished(token);
      throw err;
    }
  }, []);

  // By value: a refetched subscription with the same renewal doesn't redraw the button.
  const recurringKey = wallet === "apple_pay" && recurring ? JSON.stringify(recurring) : null;
  const optionsKey = JSON.stringify(elementsOptions);
  const slot = useCallback(
    (node: HTMLDivElement | null) => {
      if (!stripe || !node) return;
      const name = WALLET_NAMES[wallet];
      const renewal = recurringKey ? (JSON.parse(recurringKey) as ApplePayRecurring) : null;
      const elements = stripe.elements(JSON.parse(optionsKey) as Record<string, unknown>);
      let el: StripeExpressCheckoutElement;
      try {
        el = elements.create("expressCheckout", {
          ...walletButtonOptions(wallet),
          // The wallet's billing address, kept with the card like the card form's.
          billingAddressRequired: true,
          applePay: renewal
            ? {
                recurringPaymentRequest: {
                  paymentDescription: "CortexGrip plan and domain renewals",
                  managementURL: `${window.location.origin}/billing`,
                  regularBilling: {
                    amount: renewal.amountCents,
                    label: renewal.label,
                    recurringPaymentStartDate: new Date(renewal.startsAt),
                    recurringPaymentIntervalUnit: renewal.interval,
                    recurringPaymentIntervalCount: 1,
                  },
                },
              }
            : undefined,
        });
      } catch (err) {
        // Stripe refuses options it doesn't accept: say so rather than wait for a button.
        setStatus("unavailable");
        setError(err);
        return;
      }
      // Stripe reports which buttons show on `ready` and, if that changes, on
      // `availablepaymentmethodschange`; if neither comes, the wallet isn't available here.
      const settle = (shown: AvailableWallets | undefined) => {
        window.clearTimeout(gaveUp);
        setStatus((wallet === "apple_pay" ? shown?.applePay : shown?.googlePay) ? "ready" : "unavailable");
      };
      const gaveUp = window.setTimeout(
        () => setStatus((s) => (s === "loading" ? "unavailable" : s)),
        WALLET_WAIT_MS,
      );
      el.on("ready", (e) => settle(e.availablePaymentMethods));
      el.on("availablepaymentmethodschange", (e) => settle(e.paymentMethods));
      el.on("loaderror", (e) => {
        setStatus("unavailable");
        setError(new Error(e.error.message ?? `${name} couldn't load.`));
      });
      el.on("cancel", () => setSaving(false));
      el.on("confirm", (event) => {
        // One at a time: the button can be pressed again (by keyboard) while the first is finishing.
        if (busy.current) {
          event.paymentFailed({ reason: "fail" });
          return;
        }
        busy.current = true;
        setSaving(true);
        setError(null);
        let reachedStripe = false;
        void (async () => {
          try {
            const { error: invalid } = await elements.submit();
            if (invalid) throw new Error(invalid.message ?? incompleteRef.current);
            const started = await flowRef.current.start();
            reachedStripe = true;
            const token = await flowRef.current.confirm(stripe, elements, started);
            await finish(token);
          } catch (err) {
            // Before Stripe has it, the wallet's sheet is still waiting: close it as failed.
            if (!reachedStripe) event.paymentFailed({ reason: "fail" });
            setError(err);
          } finally {
            busy.current = false;
            setSaving(false);
          }
        })();
      });
      el.mount(node);
      return () => {
        window.clearTimeout(gaveUp);
        el.destroy();
        setStatus("loading");
      };
    },
    [stripe, wallet, recurringKey, optionsKey, finish],
  );

  const retry = useCallback(() => {
    if (!unfinished || busy.current) return;
    busy.current = true;
    setSaving(true);
    setError(null);
    finish(unfinished)
      .catch(setError)
      .finally(() => {
        busy.current = false;
        setSaving(false);
      });
  }, [unfinished, finish]);

  // Without Stripe there is no wallet at all.
  const connected = provider === undefined || (provider === "stripe" && !!key);
  return {
    status: connected ? status : "unavailable",
    slot,
    saving,
    error,
    retry: unfinished ? retry : null,
  };
}
